import type { ActiveFilterChip } from '@/components/ui/list-filters';
import {
  NO_PROFILE_FILTERS,
  profileFilterChips,
  profileFilterParams,
  type CompanyProfileFilterValues,
} from '@/features/crm/companies/profileFilters';
import { countryName } from '@/lib/countries';
import { icpFitLabel } from './constants';

/** Leads' qualification filters (ADR-108) — the company's profile plus the
 *  lead's own ICP fit and segment. `'all'` = not filtering; `'none'` = unset. */
export interface LeadQualificationFilterValues extends CompanyProfileFilterValues {
  country: string;
  icpFit: string;
  icpSegment: string;
}

export const NO_QUALIFICATION_FILTERS: LeadQualificationFilterValues = {
  ...NO_PROFILE_FILTERS,
  country: 'all',
  icpFit: 'all',
  icpSegment: 'all',
};

export function qualificationFilterParams(v: LeadQualificationFilterValues) {
  return {
    ...profileFilterParams(v),
    country: v.country === 'all' ? undefined : v.country,
    icp_fit: v.icpFit === 'all' ? undefined : v.icpFit,
    icp_segment: v.icpSegment === 'all' ? undefined : v.icpSegment,
  };
}

export function qualificationFilterChips(
  v: LeadQualificationFilterValues,
  onChange: (next: LeadQualificationFilterValues) => void,
  segmentName: (id: string) => string,
): ActiveFilterChip[] {
  const chips = profileFilterChips(v, (profile) => onChange({ ...v, ...profile }));
  if (v.country !== 'all') {
    chips.push({
      key: 'country',
      label: `Country: ${countryName(v.country)}`,
      onRemove: () => onChange({ ...v, country: 'all' }),
    });
  }
  if (v.icpFit !== 'all') {
    chips.push({
      key: 'icpFit',
      label: `ICP fit: ${icpFitLabel(v.icpFit === 'none' ? '' : v.icpFit)}`,
      onRemove: () => onChange({ ...v, icpFit: 'all' }),
    });
  }
  if (v.icpSegment !== 'all') {
    chips.push({
      key: 'icpSegment',
      label: `Segment: ${v.icpSegment === 'none' ? 'None' : segmentName(v.icpSegment)}`,
      onRemove: () => onChange({ ...v, icpSegment: 'all' }),
    });
  }
  return chips;
}
